import {
  applyStateDelta,
  type GameModule,
  type GameTurnInput,
  type GameTurnOutcome,
  type ViewSpec,
  type ViewWidget,
  type VoteWidget,
} from "@roqeet/core";

/**
 * 소셜 추리(마피아형) — 네이티브 게임 모듈(결정 #24/#25). 결정 #27이 "소셜 추리"용으로
 * 명시한 vote 위젯을 실증하는 네 번째 네이티브 모듈이자, GameModule 추상화가 "투표·
 * 정체 추리" 장르에도 맞음을 보이는 사례.
 *
 * 흐름: AI가 마을에 용의자 몇 명을 세우고(`@추가 suspects "이름"`), 매 라운드
 * 토론을 묘사한다. 플레이어는 vote 위젯으로 한 명을 지목하고(채팅방이 "나는 X에게
 * 투표한다"를 전송), AI가 결과를 반영한다(범인 적중 → solved, 무고한 처형 → 위험).
 *
 * 상태 모양(moduleState):
 *   { round, suspects: string[], eliminated: string[], playerAlive, solved, failed }
 */

const DEFAULT_STATE = (): Record<string, unknown> => ({
  round: 1,
  suspects: [],
  eliminated: [],
  playerAlive: true,
  solved: false,
  failed: false,
});

/** 모델에 RoQeet 게임 명령 문법을 지시(결정 #26). 투표 흐름과 현재 용의자를 알려 일관성을 높인다. */
function protocolInstructions(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): string {
  const round = num(state["round"], 1);
  const suspects = arr(state["suspects"]);
  const eliminated = arr(state["eliminated"]);
  const alive = suspects.filter((s) => !eliminated.includes(s));
  const ended = state["solved"] === true || state["failed"] === true;
  
  const tf = (k: string, fallback: string, ...args: any[]) => t ? t(k, ...args) : fallback;

  return [
    tf("game.prompt.sd.p1", "당신은 소셜 추리 게임의 진행자입니다. 인물들(AI)의 발언과 투표를 진행하세요."),
    '게임 시작 시, 개성 있는 용의자 3~4명을 세우세요: 각 인물마다 @추가 suspects "이름" 명령으로 등록하고 본문에서 소개하세요.',
    "매 라운드 인물들의 대화·알리바이·의심 정황을 생생하게 묘사하세요. 플레이어는 화면의 투표 버튼으로 한 명을 지목합니다.",
    "투표 결과 처리(정확한 문법으로):",
    '- 지목한 인물을 탈락시킴: @추가 eliminated "이름"',
    "- 지목이 범인이면(게임 승리): @설정 solved = true",
    "- 무고한 사람을 지목해 범인이 도망치거나 플레이어가 당하면(패배): @설정 failed = true",
    "- 라운드 진행: @증가 round 1",
    "긴장감 있게, 단서는 공정하게 흘리세요.",
    "",
    tf("game.prompt.sd.p4", "라운드 {round} — 게임 진행 중입니다. 플레이어의 투표 결과를 기다리거나 투표를 진행하세요.", { round }),
    `현재 — ${round}라운드, 생존 용의자: [${alive.join(", ") || tf("game.prompt.ta.p10", "아직 없음")}]${
      eliminated.length ? `, 탈락: [${eliminated.join(", ")}]` : ""
    }${ended ? tf("game.prompt.ta.p9", " (게임 종료됨)") : ""}`,
  ].join("\n");
}

/** 파싱된 턴을 상태에 반영(결정 #26/#28). */
function applyTurn(input: GameTurnInput, t?: (k: string, ...args: any[]) => string): GameTurnOutcome {
  let state = applyStateDelta(input.state, input.parsed.stateDelta);
  // 선택지가 오면 영속(드물지만 분기 안내용). 투표는 위젯이 별도로 구동.
  state = { ...state, choices: input.parsed.choices };

  const ended = state["solved"] === true || state["failed"] === true;
  const outcome: GameTurnOutcome = { state, ended };
  if (ended) outcome.endingLabel = state["solved"] === true ? "사건 해결" : "추리 실패";
  return outcome;
}

/** 현재 상태에서 렌더할 위젯 선언(결정 #27). 핵심: 생존 용의자를 vote 위젯으로. */
function viewSpec(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): ViewSpec {
  const round = num(state["round"], 1);
  const suspects = arr(state["suspects"]);
  const eliminated = arr(state["eliminated"]);
  const alive = suspects.filter((s) => !eliminated.includes(s));
  const solved = state["solved"] === true;
  const failed = state["failed"] === true;
  const ended = solved || failed;

  const hud: ViewWidget[] = [
    {
      kind: "stat-panel",
      stats: [
        { label: "Round", value: round },
        { label: "Alive", value: alive.length },
      ],
    },
  ];

  const widgets: ViewWidget[] = [];

  // 탈락자 표시(있을 때).
  if (eliminated.length > 0) {
    widgets.push({
      kind: "stat-panel",
      title: "OUT",
      stats: eliminated.map((name) => ({ label: name, value: "OUT" })),
    });
  }

  if (ended) {
    widgets.push({
      kind: "notice",
      tone: solved ? "success" : "danger",
      text: solved
        ? (t ? t("game.noticeEnd", "범인을 찾아냈습니다! 마을에 평화가 돌아왔습니다.") : "범인을 찾아냈습니다! 마을에 평화가 돌아왔습니다.")
        : (t ? t("game.noticeDeath", "범인을 놓쳤습니다. 마을은 어둠에 잠겼습니다.") : "범인을 놓쳤습니다. 마을은 어둠에 잠겼습니다."),
    });
  } else if (alive.length > 0) {
    // 생존 용의자를 투표 후보로(결정 #27 vote 위젯). id=label=이름.
    const vote: VoteWidget = {
      kind: "vote",
      title: "누가 범인일까요? 한 명을 지목하세요.",
      candidates: alive.map((name) => ({ id: name, label: name })),
    };
    widgets.push(vote);
  }

  return { widgets, hud };
}

export const socialDeductionModule: GameModule = {
  manifest: {
    id: "social-deduction",
    title: "소셜 추리",
    titleKey: "game.mod.socialDeduction.title",
    description: "마을에 숨은 범인을 투표로 찾는 AI 추리 게임. 용의자·라운드·투표.",
    descriptionKey: "game.mod.socialDeduction.desc",
    kind: "native",
    icon: "vote",
  },
  initialState: DEFAULT_STATE,
  protocolInstructions,
  applyTurn,
  viewSpec,
};

function num(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function arr(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}
